import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Link,
  Slide,
  Stack,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import CookieIcon from '@mui/icons-material/Cookie';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import SettingsIcon from '@mui/icons-material/Settings';

const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true and can't be changed
    analytics: true,
    marketing: false,
    preferences: true,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consentStatus = localStorage.getItem('cookieConsent');
    if (!consentStatus) {
      // Wait a moment before showing the banner for better UX
      const timer = setTimeout(() => {
        setOpen(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      // Apply saved preferences
      try {
        const savedPreferences = JSON.parse(localStorage.getItem('cookiePreferences'));
        if (savedPreferences) {
          setPreferences(savedPreferences);
        }
      } catch (error) {
        console.error('Error parsing cookie preferences:', error);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setOpen(false);
    
    // If using Google Analytics, enable it
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'functionality_storage': 'granted',
        'personalization_storage': 'granted',
      });
    }
  };

  const handleRejectNonEssential = () => {
    const minimalPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    localStorage.setItem('cookieConsent', 'minimal');
    localStorage.setItem('cookiePreferences', JSON.stringify(minimalPreferences));
    setPreferences(minimalPreferences);
    setOpen(false);
    
    // If using Google Analytics, disable it
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'functionality_storage': 'denied',
        'personalization_storage': 'denied',
      });
    }
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', 'custom');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setOpen(false);
    setShowDetails(false);
    
    // If using Google Analytics, update consent based on preferences
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied',
        'ad_storage': preferences.marketing ? 'granted' : 'denied',
        'functionality_storage': preferences.preferences ? 'granted' : 'denied',
        'personalization_storage': preferences.preferences ? 'granted' : 'denied',
      });
    }
  };

  const handlePreferenceChange = (type) => {
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleManageCookies = () => {
    setShowDetails(!showDetails);
  };

  // Function to open cookie settings from anywhere in the app
  window.openCookieSettings = () => {
    setOpen(true);
    setShowDetails(true);
  };

  if (!open) return null;

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          p: 3,
          borderRadius: { xs: 0, sm: '16px 16px 0 0' },
          maxWidth: { sm: '600px', md: '800px' },
          mx: 'auto',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CookieIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
            <Typography variant="h6" component="h2">
              Cookie Preferences
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setOpen(false)} 
            aria-label="close"
            size="small"
            sx={{ mt: -1, mr: -1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography variant="body2" paragraph>
          We use cookies to enhance your experience, analyze site traffic, and personalize content. 
          By clicking "Accept All", you consent to our use of cookies. You can manage your preferences 
          or reject non-essential cookies by clicking the respective buttons.
        </Typography>

        <Link 
          component={RouterLink} 
          to="/cookies" 
          color="primary" 
          underline="hover"
          sx={{ display: 'inline-block', mb: 2, fontSize: '0.875rem' }}
        >
          Learn more in our Cookie Policy
        </Link>

        {showDetails && (
          <Box sx={{ mb: 3, mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Customize Cookie Preferences
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Stack spacing={2} divider={<Divider flexItem />}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="bold">
                      Necessary Cookies
                    </Typography>
                    <Tooltip title="Always active" arrow placement="top">
                      <CheckCircleIcon color="success" fontSize="small" />
                    </Tooltip>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    These cookies are essential for the website to function properly and cannot be disabled.
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="bold">
                      Analytics Cookies
                    </Typography>
                    <Button 
                      size="small" 
                      variant={preferences.analytics ? "contained" : "outlined"}
                      color={preferences.analytics ? "primary" : "inherit"}
                      onClick={() => handlePreferenceChange('analytics')}
                      startIcon={preferences.analytics ? <CheckCircleIcon /> : <BlockIcon />}
                      sx={{ minWidth: '100px' }}
                    >
                      {preferences.analytics ? 'Enabled' : 'Disabled'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    These cookies help us understand how visitors interact with our website, helping us improve our services.
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="bold">
                      Marketing Cookies
                    </Typography>
                    <Button 
                      size="small" 
                      variant={preferences.marketing ? "contained" : "outlined"}
                      color={preferences.marketing ? "primary" : "inherit"}
                      onClick={() => handlePreferenceChange('marketing')}
                      startIcon={preferences.marketing ? <CheckCircleIcon /> : <BlockIcon />}
                      sx={{ minWidth: '100px' }}
                    >
                      {preferences.marketing ? 'Enabled' : 'Disabled'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    These cookies are used to track visitors across websites to display relevant advertisements.
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="bold">
                      Preferences Cookies
                    </Typography>
                    <Button 
                      size="small" 
                      variant={preferences.preferences ? "contained" : "outlined"}
                      color={preferences.preferences ? "primary" : "inherit"}
                      onClick={() => handlePreferenceChange('preferences')}
                      startIcon={preferences.preferences ? <CheckCircleIcon /> : <BlockIcon />}
                      sx={{ minWidth: '100px' }}
                    >
                      {preferences.preferences ? 'Enabled' : 'Disabled'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    These cookies enable personalized features and remember your preferences across our website.
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        )}

        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={1} 
          justifyContent="space-between"
          sx={{ mt: 2 }}
        >
          <Button 
            variant="outlined" 
            color="inherit" 
            size="small"
            onClick={handleRejectNonEssential}
            startIcon={<BlockIcon />}
            sx={{ order: { xs: 3, sm: 1 }, flexGrow: { xs: 1, sm: 0 } }}
          >
            Reject Non-Essential
          </Button>
          
          <Button 
            variant="outlined" 
            color="primary" 
            size="small"
            onClick={handleManageCookies}
            startIcon={<SettingsIcon />}
            sx={{ order: { xs: 2, sm: 2 }, flexGrow: { xs: 1, sm: 0 } }}
          >
            {showDetails ? 'Hide Details' : 'Customize'}
          </Button>
          
          {showDetails ? (
            <Button 
              variant="contained" 
              color="primary" 
              size="small"
              onClick={handleSavePreferences}
              startIcon={<CheckCircleIcon />}
              sx={{ order: { xs: 1, sm: 3 }, flexGrow: { xs: 1, sm: 0 } }}
            >
              Save Preferences
            </Button>
          ) : (
            <Button 
              variant="contained" 
              color="primary" 
              size="small"
              onClick={handleAcceptAll}
              startIcon={<CheckCircleIcon />}
              sx={{ order: { xs: 1, sm: 3 }, flexGrow: { xs: 1, sm: 0 } }}
            >
              Accept All
            </Button>
          )}
        </Stack>
      </Paper>
    </Slide>
  );
};

export default CookieConsent;
